// 4. 10. 2026 (package 3, A–E, 8.a and 11.f): the end of a season and what carries over.
// Owner decisions (docs/ANALIZA_slicne_igre.md, chapter 14):
//  - An Ascension wipe at the end of every season. The account keeps the Collection book, outfits, titles, frames,
//    achievements, the Survivor Rank (number and cosmetics), name, look, friends, the linked login, Legacy points and
//    perks and the season rewards. The survivor starts over: level and XP, attributes, the tree, all gear and items
//    (Relic Forge Legendaries and Pristine items too), learned recipes, Storage, buildings, energy (as a new player),
//    contracts, the season path, Relic Shards, keys, tracks, Tobias, the daily reward and the Veteran Ledger. History
//    (reports, the feed, the fight log) stays. The background can be chosen again (name and look stay).
//  - Legacy points: claimed season path goals ÷ 4, rounded down, at most 7 a season; kept for good. A perk is bought
//    once and works in every season after.
//  - Season Score: points for the path, the horde, fights won, secret zones found first, new book fields and
//    expeditions; a participant has at least 300. Places bring the rewards (C).

import type { RecipeBook } from './items/gearRecipes';
import { SEASON_GOALS, seasonName } from './season';

/**
 * The end of a season by the end job (scores, rewards, the wipe, the next season). `on: false` (simulations,
 * `useRulesBefore4Oct`): package 2, the next season starts by itself and nothing is reset.
 */
export const SEASON_END = { on: true };

// ---- A. Legacy perks -------------------------------------------------------------------------------------------

export type LegacyPerkId = 'workshop2' | 'storage3' | 'treeL4' | 'respec' | 'starterPack' | 'barricade1' | 'keepRecipes';

export interface LegacyPerk {
  id: LegacyPerkId;
  name: string;
  text: string;
  price: number;
}

/** Every perk: none touches combat or Power. The prices add up to 12 (the owner's proposal; the simulation checks them). */
export const LEGACY_PERKS: LegacyPerk[] = [
  { id: 'workshop2', name: 'Head Start: Workshop', text: 'Start every season with Workshop 2.', price: 1 },
  { id: 'storage3', name: 'Head Start: Storage', text: 'Start every season with Storage 3.', price: 2 },
  { id: 'treeL4', name: 'Early Skills', text: 'The skill tree opens at level 4 instead of 5.', price: 2 },
  { id: 'respec', name: 'Second Thoughts', text: 'One free respec of attributes and the skill tree every season.', price: 1 },
  { id: 'starterPack', name: 'Go Bag', text: 'Start every season with 5 Bandages, 4 Rations and 50 Scrap.', price: 1 },
  { id: 'barricade1', name: 'Head Start: Barricade', text: 'Start every season with Barricade 1, so your scouts go out from day one.', price: 3 },
  { id: 'keepRecipes', name: 'Keep 3 Recipes', text: 'When a season ends, keep 3 recipes you learned.', price: 2 },
];

export const LEGACY_PERK = Object.fromEntries(LEGACY_PERKS.map((p) => [p.id, p])) as Record<LegacyPerkId, LegacyPerk>;

export const LEGACY_RULES = {
  /** Claimed path goals per Legacy point. */
  perGoals: 4,
  maxPerSeason: 7,
  /** The Go Bag. */
  starterPack: { bandage: 5, ration: 4, scrap: 50 },
  /** Recipes "Keep 3 Recipes" keeps. */
  keepRecipes: 3,
};

/** The account's Legacy: points earned in all seasons (by season), perks bought. */
export interface LegacyState {
  earned: Record<string, number>;
  perks: LegacyPerkId[];
}

export const EMPTY_LEGACY: LegacyState = { earned: {}, perks: [] };

/** Legacy points for a season: claimed goals ÷ 4, rounded down, at most 7. */
export function legacyPointsFor(claimedGoals: number): number {
  return Math.min(LEGACY_RULES.maxPerSeason, Math.floor(Math.max(0, claimedGoals) / LEGACY_RULES.perGoals));
}

export function legacyEarned(l: LegacyState | null | undefined): number {
  return Object.values(l?.earned ?? {}).reduce((a, b) => a + b, 0);
}

export function legacySpent(l: LegacyState | null | undefined): number {
  return (l?.perks ?? []).reduce((a, id) => a + (LEGACY_PERK[id]?.price ?? 0), 0);
}

export function legacyAvailable(l: LegacyState | null | undefined): number {
  return legacyEarned(l) - legacySpent(l);
}

export const hasLegacy = (l: LegacyState | null | undefined, id: LegacyPerkId) => !!l?.perks.includes(id);

/** Why this perk cannot be bought now, or null. */
export function legacyBuyBlocker(l: LegacyState | null | undefined, id: LegacyPerkId): string | null {
  const p = LEGACY_PERK[id];
  if (!p) return 'No such perk.';
  if (hasLegacy(l, id)) return 'You have it already.';
  const have = legacyAvailable(l);
  if (have < p.price) return `${p.price - have} Legacy point${p.price - have === 1 ? '' : 's'} short`;
  return null;
}

// ---- B. Season Score ---------------------------------------------------------------------------------------

export type ScoreCat = 'goals' | 'horde' | 'pvp' | 'secrets' | 'book' | 'runs';

export const SCORE_RULES = {
  /** A claimed season path goal (40 since the owner's decision of 5. 10. 2026: at 100 the path was 57–61% of an active
   * player's points in sim:progress, at 40 it is 34–38%). */
  goal: 40,
  /** A horde (or the Sunday boss) by the personal threshold reached: 1 / 2 / 3. */
  horde: [20, 40, 60] as const,
  /** A win over a real player or their track; at most this many a day; never from the same IP. */
  pvp: 20,
  pvpPerDay: 3,
  /** The first discovery of a secret zone in the world. */
  secret: 300,
  /** A new field in the Collection book this season. */
  bookField: 5,
  /** A finished expedition; at most `runsPerDay` points a day (10 runs). 3 and 30 since the owner's decision of 5. 10. 2026
   * (at 2 and 20 the Collection book was 41% of the Crafter's points in the final pair, over the 40% limit; at 3 it is 38%). */
  run: 3,
  runsPerDay: 30,
  /** A participant has at least this many. */
  participant: 300,
  /** Top: at most 10 and at most 10% of the participants, at least 1. Veterans: the top 25%. */
  top: 10,
  topShare: 0.1,
  veteranShare: 0.25,
};

export const SCORE_CATS: { id: ScoreCat; name: string }[] = [
  { id: 'goals', name: 'Season path' },
  { id: 'horde', name: 'Horde' },
  { id: 'pvp', name: 'Fights won' },
  { id: 'secrets', name: 'Secret zones found' },
  { id: 'book', name: 'Collection book' },
  { id: 'runs', name: 'Expeditions' },
];

export type ScoreCats = Record<ScoreCat, number>;

export const emptyScore = (): ScoreCats => ({ goals: 0, horde: 0, pvp: 0, secrets: 0, book: 0, runs: 0 });

export const scoreTotal = (c: Partial<ScoreCats>) => SCORE_CATS.reduce((a, { id }) => a + (c[id] ?? 0), 0);

/** Points of one horde by the highest personal threshold reached (0–3). */
export const hordeScore = (threshold: number) => (threshold > 0 ? SCORE_RULES.horde[Math.min(3, threshold) - 1] : 0);

/** How many champions (top) a season has. */
export function championCount(participants: number): number {
  if (participants <= 0) return 0;
  return Math.max(1, Math.min(SCORE_RULES.top, Math.floor(participants * SCORE_RULES.topShare)));
}

/** The rewards of a place (1 = first) among `participants`; none for a survivor who is not a participant. */
export function placeRewards(place: number | null, participants: number): { champion: boolean; veteran: boolean } {
  if (!place || place > participants) return { champion: false, veteran: false };
  return { champion: place <= championCount(participants), veteran: place <= Math.ceil(participants * SCORE_RULES.veteranShare) };
}

/** "Top 12%": the share of participants at or above this place. */
export function placePercentile(place: number, participants: number): number {
  return participants > 0 ? Math.max(1, Math.ceil((place / participants) * 100)) : 100;
}

// ---- C. Rewards --------------------------------------------------------------------------------------------

export const SEASON_REWARD_RULES = {
  /** Claimed path goals for the Relic Discount. */
  discountGoals: 20,
};

export const championTitle = (season: number) => `${seasonName(season)} Champion`;
export const veteranTitle = (season: number) => `${seasonName(season)} Veteran`;
export const championFrame = (season: number) => `season_${season}_champion`;
export const veteranFrame = (season: number) => `season_${season}_veteran`;
/** The label a champion's outfit carries in the book and on Appearance. */
export const championOutfitLabel = (season: number) => `${seasonName(season)} champion`;

/** What one season left the account (users.season_rewards). */
export interface SeasonEndReward {
  season: number;
  place: number | null;
  points: number;
  cats: ScoreCats;
  participants: number;
  goals: number;
  champion: boolean;
  veteran: boolean;
  /** The first Relic Forge of the next season costs 40 (true until it is used). */
  relicDiscount: boolean;
  relicDiscountUsed?: boolean;
  legacyPoints: number;
  /** A champion's Legendary outfit: the set chosen (null until chosen). */
  outfit?: { set: string | null } | null;
  /** The end screen was seen. */
  seen?: boolean;
}

// ---- D. The end of a season ------------------------------------------------------------------------------

/**
 * A survivor after its season ended (survivors.season_end), until the player walks out into the new one: the season
 * that ended, the champion's outfit still to choose, the recipes "Keep 3 Recipes" can keep (the old book) and the
 * ones kept. `walkedOut`: the new season has begun for this survivor (the choices left wait on the Season screen).
 */
export interface SeasonEndState {
  season: number;
  walkedOut: boolean;
  outfit: boolean;
  keep: RecipeBook | null;
  kept: string[];
}

export const SEASON_END_RULES = {
  /** The Camp card from this many days before the end; notifications on these days. */
  cardDays: 14,
  notifyDays: [14, 7, 1] as const,
  /** The last hours: a run that would end after the season cannot be sent. */
  lastHours: 24,
  /** Season 1 ends on 17. 11. 2026 at 20:30 server time (after the horde). */
  season1End: '2026-11-17T20:30:00',
};

export const SEASON_KEEPS = [
  'Collection book, outfits, titles and frames',
  'Achievements and your Survivor Rank',
  'Name, look, friends and your linked login',
  'Legacy points and perks',
  'Season rewards (champion outfit, Relic Discount)',
  'Reports, the feed and the fight log',
];

export const SEASON_RESETS = [
  'Level, XP, attributes and the skill tree',
  'All gear and items, Relic Forge and Pristine items too',
  'Learned recipes, Storage and buildings',
  'Energy (as a new survivor), contracts and the season path',
  'Relic Shards, keys and your tracks',
  'Tobias, the daily reward and the Veteran Ledger',
];

/** "Season 0 ends in 13 days" (in hours on the last day; the shown name, `seasonName`). */
export function seasonEndsText(season: number, endsAt: number, now: number): string {
  const ms = endsAt - now;
  const name = seasonName(season);
  if (ms <= 0) return `${name} is over`;
  const h = Math.ceil(ms / 3_600_000);
  if (h < 24) return `${name} ends in ${h} hour${h === 1 ? '' : 's'}`;
  const d = Math.floor(ms / 86_400_000);
  return `${name} ends in ${d} day${d === 1 ? '' : 's'}`;
}

/** A run sent now for `minutes` ends after the season does. */
export const runEndsAfterSeason = (now: number, minutes: number, endsAt: number) => now + minutes * 60_000 > endsAt;

export const SEASON_RUN_BLOCK = 'This run ends after the season does. It brings nothing back.';

// ---- E. After the last goal (11.f) -------------------------------------------------------------------------

/** After the last path goal is claimed: every 4 daily contracts claimed bring a Supply crate (no XP, no energy). */
export const SUPPLY_CRATE = { dailies: 4, scrap: 100, consumables: 2, rareChance: 0.1 };

export const LAST_GOAL = SEASON_GOALS.length;
